import {
  Box,
  Chip,
  CircularProgress,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AvailabilityCard from "app/components/Business/AvailabilityCard";
import DatePickerModal from "app/components/Business/DatePickerModal";
import FareharborAvailabilityCard from "app/components/Business/FareharborAvailabilityCard";
import CheckoutBusinessCard from "app/components/BusinessCheckout/CheckoutBusinessCard";
import { CheckoutHeader } from "app/components/BusinessCheckout/CheckoutHeader";
import CheckoutToggle from "app/components/BusinessCheckout/CheckoutToggle";
import ReferreLogo from "assets/icons/Referee_B_Logo.svg";
import dayjs, { Dayjs } from "dayjs";
import { Business } from "lib/features/business/types";
import {
  useGetBusinessListByIdQuery,
  useLazyGetBookableItemsByIdAndDateQuery,
} from "lib/features/businessList/businessListService";

import { BusinessCheckoutTabs } from "lib/features/businessList/types";
import { Item } from "lib/features/item/types";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { checkoutStyles as styles } from "styles/Checkout/checkout.styles";

const BusinessCheckout = () => {
  const theme = useTheme();
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState<string>(
    BusinessCheckoutTabs.BookableItems
  );
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  const [isCalendarVisible, setIsCalendarVisible] = useState<boolean>(false);
  const [loadingItem, setLoadingItem] = useState<boolean>(false);
  // ---- Business Filter Logic ----
  const [selectedBusiness, setSelectedBusiness] = useState<string>("All");

  const { data, isLoading, isError } = useGetBusinessListByIdQuery({
    id: Number(id),
  });

  const [
    getBookableItemsByIdAndDate,
    {
      data: bookableItems,
      isLoading: bookableItemsLoading,
      isFetching: bookableItemsFetching,
    },
  ] = useLazyGetBookableItemsByIdAndDateQuery();

  const handleDateChange = (newDate: Dayjs | null) => {
    if (newDate) {
      setSelectedDate(dayjs(newDate));
    }
    setIsCalendarVisible(false);
  };

  useEffect(() => {
    if (id && selectedDate) {
      try {
        setLoadingItem(true);
        getBookableItemsByIdAndDate({
          id: Number(id),
          date: selectedDate.format("YYYY-MM-DD"),
        });
      } catch (error) {
        console.log("error: ", error);
      } finally {
        setLoadingItem(false);
      }
    }
  }, [selectedDate]);

  const itemsByBusiness = useMemo(() => {
    if (!bookableItems) return [];

    type Group = { business: Business; items: Item[] };

    const map = new Map<number, Group>();

    bookableItems.forEach((it) => {
      const biz = it.business;
      if (!map.has(biz.id)) {
        map.set(biz.id, { business: biz, items: [] });
      }
      map.get(biz.id)!.items.push(it);
    });

    return Array.from(map.values());
  }, [bookableItems]);

  const businessNames = useMemo(() => {
    if (!data?.businesses) return [];
    // ✅ Using Array.from() instead of spread to avoid target issues
    return Array.from(new Set(data.businesses.map((b) => b.business)));
  }, [data?.businesses]);

  const filteredBusinesses = useMemo(() => {
    if (!data?.businesses) return [];
    if (selectedBusiness === "All") return data.businesses;
    return data.businesses.filter((b) => b.business === selectedBusiness);
  }, [selectedBusiness, data?.businesses]);

  const isScreenBreak = useMediaQuery("(max-width: 842px)");

  return (
    <Box sx={styles.multiBusinessCheckoutContainer(theme)}>
      {isLoading ? (
        <Box sx={styles.loaderContainer}>
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Typography>Internal Server Error</Typography>
      ) : (
        <>
          {/* Header */}
          {data && (
            <CheckoutHeader
              userData={data?.user}
              listHeadline={data?.list_headline}
            />
          )}

          {/* Toggle */}
          <CheckoutToggle activeTab={activeTab} setActiveTab={setActiveTab} />

          {/* Calender */}

          {activeTab === BusinessCheckoutTabs.BookableItems && (
            <Box sx={styles.calendarContainer}>
              <DatePickerModal
                date={selectedDate}
                handleDateChange={handleDateChange}
                isCalendarVisible={isCalendarVisible}
                setIsCalendarVisible={setIsCalendarVisible}
              />
            </Box>
          )}

          {/* Cards */}
          {data ? (
            activeTab === BusinessCheckoutTabs.BookableItems ? (
              <Box>
                {bookableItemsLoading || bookableItemsFetching ? (
                  <Box sx={styles.centeredBox}>
                    <CircularProgress size={32} />
                  </Box>
                ) : bookableItems?.length === 0 ? (
                  <Box sx={styles.centeredBox}>
                    <Typography>No Record Found</Typography>
                  </Box>
                ) : (
                  itemsByBusiness.map(({ business, items }) => (
                    <Box key={business.id} sx={styles.businessSectionWrapper}>
                      <Typography variant="h3" sx={styles.businessSectionTitle}>
                        {business.business}
                      </Typography>
                      <Box sx={styles.cardRow(isScreenBreak)}>
                        {items.map((item) =>
                          item.item_type === 1 ? (
                            <Box
                              key={item.id}
                              sx={styles.businessItemCardWrapper}
                            >
                              <AvailabilityCard
                                item={item}
                                user_id={data.user.id}
                                referredBy={data.user.id}
                              />
                            </Box>
                          ) : item.item_type === 4 &&
                            item.fareharbor_availabilities?.length ? (
                            <Box
                              key={item.id}
                              sx={styles.businessItemCardWrapper}
                            >
                              <FareharborAvailabilityCard
                                item={item}
                                user_id={data.user.id}
                                referredBy={data.user.id}
                                screenName="Business Checkout"
                              />
                            </Box>
                          ) : null
                        )}
                      </Box>
                    </Box>
                  ))
                )}
              </Box>
            ) : (
              <Box sx={{ mb: 2 }}>
                {/* Filter Chips */}
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                  <Chip
                    label="All"
                    onClick={() => setSelectedBusiness("All")}
                    variant={selectedBusiness === "All" ? "filled" : "outlined"}
                    color={selectedBusiness === "All" ? "primary" : "default"}
                  />
                  {businessNames.map((name) => (
                    <Chip
                      key={name}
                      label={name}
                      onClick={() => setSelectedBusiness(name)}
                      variant={
                        selectedBusiness === name ? "filled" : "outlined"
                      }
                      color={selectedBusiness === name ? "primary" : "default"}
                      sx={{
                        borderRadius: "16px",
                        fontWeight: 500,
                      }}
                    />
                  ))}
                </Box>

                {/* Filtered Business Cards */}
                <Box sx={styles.businessCardContainer}>
                  {filteredBusinesses.length > 0 ? (
                    filteredBusinesses.map((business: Business) => (
                      <CheckoutBusinessCard
                        key={business.id}
                        business={business}
                        userData={data.user}
                      />
                    ))
                  ) : (
                    <Typography>No businesses found.</Typography>
                  )}
                </Box>
              </Box>
            )
          ) : (
            <Typography>
              Internal Server Error. Please refresh the page.
            </Typography>
          )}

          {/* Powered By */}
          <Box sx={styles.poweredByContainer}>
            <Typography sx={styles.poweredByText}>Powered by</Typography>
            <img src={ReferreLogo} width={122} alt="" />
          </Box>
        </>
      )}
    </Box>
  );
};

export default BusinessCheckout;
